import {
  Component, ElementRef, AfterViewInit, ViewChild, ViewEncapsulation, ContentChildren, QueryList, OnInit,
  AfterContentInit,
  OnDestroy
} from '@angular/core';
import { animationFrame } from 'rxjs/scheduler/animationFrame';
import { Observable } from 'rxjs/Observable';
import { CuiSwipeableActionDirective } from './swipeable-action.directive';
import { CuiBaseComponent } from '../shared/base-component';
import 'rxjs/add/observable/fromEvent';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import {CuiIconService} from '../icon/icon.service';

const actionWidth = 80;
const activateThresholdPercent = .25;
const openThreshold = 100;

@Component({
  selector: 'cui-swipeable',
  templateUrl: './swipeable.component.html',
  styleUrls: ['./swipeable.component.scss']
})
export class CuiSwipeableComponent extends CuiBaseComponent implements AfterViewInit, AfterContentInit, OnInit, OnDestroy {
  leftActions: CuiSwipeableActionDirective[];
  rightActions: CuiSwipeableActionDirective[];

  manager: HammerManager;

  panning = false;
  active = false;
  panningPosition = 0;
  panningDirection: 'left' | 'right' = 'left';

  @ViewChild('item')
  itemContainer: ElementRef;

  @ContentChildren(CuiSwipeableActionDirective)
  actions: QueryList<CuiSwipeableActionDirective>;

  constructor(public elementRef: ElementRef, iconService: CuiIconService) {
    super();

    iconService.useIcons([
      'icon/chevron-left-white',
      'icon/chevron-right-white'
    ]);
  }

  ngOnInit() {
    this.manager = new Hammer(this.elementRef.nativeElement, {
      recognizers: [
        [Hammer.Pan, { direction: Hammer.DIRECTION_HORIZONTAL }]
      ]
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.manager.destroy();
  }

  ngAfterViewInit() {
    const panStartSub = Observable.fromEvent(this.manager as any, 'panstart').subscribe((e: any) => this.handlePanStart(e));

    const panSub = Observable.fromEvent(this.manager as any, 'panleft panright').subscribe((e: any) => this.handleHorizontalPan(e));

    const panEndSub = Observable.fromEvent(this.manager as any, 'panend').subscribe((e: any) => this.handlePanEnd(e));

    this.pushSubscription(panStartSub, panSub, panEndSub);
  }

  ngAfterContentInit() {
    const actions$ = Observable.merge(Observable.of(this.actions.toArray()), this.actions.changes);

    // Close the swipeable when an action is executed
    const activateSub = actions$
      .map(actions => actions.map((action: CuiSwipeableActionDirective) => action.execute$))
      .switchMap(execute => Observable.merge(...execute))
      .subscribe(() => this.close());

    // Populate leftActions and rightActions
    const parseSub = actions$.subscribe(() => this.parseActions());

    this.pushSubscription(activateSub, parseSub);
  }

  close() {
    this.panningPosition = 0;
    this.setPosition(this.panningPosition);
  }

  parseActions() {
    this.leftActions = this.actions.filter(action => action.position === 'left');
    this.rightActions = this.actions.filter(action => action.position === 'right');
  }

  handlePanStart(e: HammerInput) {
    if (Math.abs(e.deltaY) < 10) {
      this.panning = true;
    }
  }

  handleHorizontalPan(e: HammerInput) {
    if (!this.panning) {
      return;
    }

    if (this.leftActions.length > 0 && this.panningPosition + e.deltaX > 0) {
       this.panningDirection = 'left';
    }

    if (this.rightActions.length > 0 && this.panningPosition + e.deltaX < 0) {
        this.panningDirection = 'right';
    }

    const position = this.panningPosition + e.deltaX;
    const direction = position >= 0 ? 1 : -1;
    const actions = position < 0 ? this.rightActions.length : this.leftActions.length;

    this.active = actions > 1 || Math.abs(position) > window.innerWidth * activateThresholdPercent;

    if (actions === 1 || actions > 1 && Math.abs(position) < actionWidth * actions) {
      this.setPosition(this.panningPosition + e.deltaX);
    } else if (actions > 1) {
      this.setPosition(actionWidth * actions * direction);
    } else {
      this.setPosition(0);
    }
  }

  handlePanEnd(e: HammerInput) {
    if (!this.panning) {
      return;
    }

    this.panning = false;

    const position = this.panningPosition + e.deltaX;
    const direction = position >= 0 ? 1 : -1;
    const actions = position < 0 ? this.rightActions.length : this.leftActions.length;

    if (actions === 1 && Math.abs(position) > window.innerWidth * activateThresholdPercent) {
      const action = direction > 0 ? this.leftActions[0] : this.rightActions[0];
      action.execute();
    } else if (actions > 1 && Math.abs(position) > openThreshold) {
      this.panningPosition = actionWidth * actions * direction;
      this.setPosition(this.panningPosition);
    } else {
      this.close();
    }
  }

  setPosition(position: number) {
    requestAnimationFrame(() => this.itemContainer.nativeElement.style.transform = `translate3d(${position}px, 0 , 0)`);
  }
}



// WEBPACK FOOTER //
// ./node_modules/@cicero/webui/lib/core/swipeable/swipeable.component.ts